import produce from 'immer';
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { RESET_SUBMISSION_FAILURE } from '../../constants/actions/registerShipmentFormActions';
import media from '../../constants/media';
import { getQueryParam, removeURLParameter } from '../../helpers/url/urlHelpers';
import { Column, Columns } from '../base/grid';
import { asHeading3, H2 } from '../base/typography';
import WizardSteps from './wizardSteps';

interface Props {
	onSubmit?: any;
	markLastStepAsCompleted?: boolean;
	children: ReactNode[];
	hideWizardSteps?: boolean;
	internationalShipment?: boolean;
	isRegShipment?: boolean;
	side?: 'left' | 'right';
	initialStep?: number;
	disablePrevStep?: boolean;
}

interface IValues {
	[key: string]: any;
}

export interface WizardPageProps {
	onSubmit?: any;
	onPrevious?: any;
	setPage?: any;
	name: string;
}

const updateValues = (values: IValues, stepValues: any, page: any) => {
	const newValues = produce(values, draft => {
		draft[page] = stepValues;
	});

	return newValues;
};

const StepName = styled(H2)`
    margin: 4rem 0 2rem;
	${asHeading3}

	${media.tablet`
		display: none;
	`};
`;

const Wizard: FC<Props> = ({ children, hideWizardSteps, internationalShipment, isRegShipment, side='left', initialStep = 0, disablePrevStep=false }) => {
	// `?step3` is how the shipment calculator hands the visitor straight into the third step. Read it
	// once, into the initial state, so the wizard never paints step one before jumping forward.
	const [redirectedToStep3] = useState<boolean>(() => Boolean(getQueryParam('step3')));
	const [page, setPage] = useState<number>(redirectedToStep3 ? 2 : initialStep);
	const [values, setValues] = useState<IValues>({});

	useEffect(() => {
		// Don't undo the calculator redirect: this effect also fires on mount, and `initialStep` only
		// ever settles back to 0.
		if (redirectedToStep3) return;

		setPage(initialStep);
	}, [initialStep, redirectedToStep3]);

	const isLastPage =
		!internationalShipment && isRegShipment
			? page === React.Children.count(children) - 2
			: page === React.Children.count(children) - 1;
	// Scrolling to top on page changes
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [page]);

	const dispatch = useDispatch<any>();
	const next = () => {
		setPage(Math.min(page + 1, children.length - 1));
	};

	// Stable across renders so the popstate effect below does not have to re-subscribe (and re-push a
	// history entry) every time the wizard re-renders.
	const prev = useCallback(async () => {
		setPage(current => Math.max(current - 1, 0));
		dispatch({ type: RESET_SUBMISSION_FAILURE });
	}, [dispatch]);

	const set = (value: number) => {
		if (value > children.length || value < 0) return;
		setPage(value);
		dispatch({ type: RESET_SUBMISSION_FAILURE });
	};

	const onStepSubmit = stepValues => {
		const newValues = updateValues(values, stepValues, page);
		setValues(newValues);
		if (!isLastPage) next();
	};

	// Drop the calculator's parameter from the address bar once it has been consumed. This used to run
	// in the render body, which rewrote history on every single re-render.
	// This can be changed by setting the redirect into the reducer instead of as a parameter.
	useEffect(() => {
		if (!redirectedToStep3) return;

		window.history.replaceState({}, '', removeURLParameter('step3'));
	}, [redirectedToStep3]);

	const activePage = React.Children.toArray(children)[page];
	const steps = React.Children.toArray(children).map((child, index: number) => {
		const element = child as React.ReactElement<WizardPageProps>;
		return {
			name: element.props.name || `Step ${index}`,
		};
	});
	// @ts-ignore
	const activePageWithProps = React.cloneElement(activePage, {
		onSubmit: onStepSubmit,
		onPrevious: prev,
		setPage: setPage,
	});

	// Go back by wizard step on browser back.
	//
	// This effect had no dependency array, so every single render pushed another history entry and
	// attached another popstate listener that was never removed. Typing in a step's form was enough to
	// blow past the browser cap of 100 pushState calls per 10 seconds, and each leaked listener stepped
	// the wizard back again on a single Back press. One sentinel entry for the whole intermediate-step
	// stretch is all this needs.
	const interceptBrowserBack = page > 0 && page !== children.length - 1 && !disablePrevStep;

	useEffect(() => {
		if (!interceptBrowserBack) return;

		window.history.pushState(null, document.title, window.location.href);

		const onPopState = () => {
			window.history.pushState(null, document.title, window.location.href);
			prev();
		};

		window.addEventListener('popstate', onPopState);

		return () => {
			window.removeEventListener('popstate', onPopState);
		};
	}, [interceptBrowserBack, prev]);

	return (
		<Columns>
			<StyledColumn desktopSmall={1 / 4} side={side==='right' ? 1 : 0}>
				{!hideWizardSteps && (
					<>
						<WizardSteps
							steps={steps}
							onStepClick={set}
							markLastStepAsCompleted
							page={page}
							onLastStep={isLastPage}
						/>
						<StepName>{steps[page].name}</StepName>
					</>
				)}
			</StyledColumn>
			<StyledColumn desktopSmall={3 / 4} side={side==='right' ? 0 : 1}>
				{activePageWithProps}
			</StyledColumn>
		</Columns>
	);
};

const StyledColumn = styled(Column)`
    ${props => media.desktopSmall` order: ${props.side}`}
`

export default Wizard;
